import { useState, useRef, useEffect } from "react";
import jsPDF from "jspdf";

// ── Constants ─────────────────────────────────────────────────────────────────

// Defense-in-depth safety blocklist. Same patterns as the server in
// /api/google-trends.js — we check on the client too to avoid wasting an API
// call (and a search-quota slot) on obviously harmful queries. These are
// intent-shaped patterns ("how to make a bomb"), NOT broad keywords ("bomb"),
// so legitimate adjacent content like history, fiction, or mental-health
// discussion passes through.
const HARMFUL_INTENT_PATTERNS = [
  /\b(commit\s+suicide|kill\s+(my|your)self|hang\s+(my|your)self|end\s+(my|your)\s+life|painless\s+suicide|suicide\s+method|how\s+to\s+(commit\s+suicide|kill\s+myself)|overdose\s+(myself|to\s+die))\b/i,
  /\b(make\s+a\s+bomb|build\s+a\s+bomb|how\s+to\s+make\s+(a\s+bomb|explosives?|tnt|pipe\s+bomb|ied|nail\s+bomb)|bomb[-\s]?making|build\s+(explosives?|ied))\b/i,
  /\b(3d\s+print(ed)?\s+(gun|firearm|pistol|rifle)|ghost\s+gun|untraceable\s+(gun|firearm)|make\s+a\s+gun\s+at\s+home)\b/i,
  /\b(synthesize\s+(meth|methamphetamine|heroin|fentanyl|cocaine|mdma)|how\s+to\s+(make|cook|produce)\s+(meth|methamphetamine|crack|heroin|fentanyl)|home\s+meth\s+lab)\b/i,
  /\b(how\s+to\s+(kill|murder|poison)\s+(someone|a\s+person|my\s+(wife|husband|boss|neighbor|family))|get\s+away\s+with\s+murder|untraceable\s+poison)\b/i,
  /\b(child\s+(porn|pornography|sexual)|csam|underage\s+(sex|nude|porn)|loli|grooming\s+(children|kids|minors)\s+for)\b/i,
  /\b(plan\s+a?\s*(terror|terrorist)\s+attack|how\s+to\s+(commit|plan)\s+(terror|mass\s+(shoot|kill)|genocide)|mass\s+casualty\s+attack)\b/i,
];

function isHarmfulQuery(text) {
  return HARMFUL_INTENT_PATTERNS.some(p => p.test(String(text || "")));
}

// Defensive sanity check on any string we *think* is a niche. Catches the
// "[object Object]" footgun where a click event or other non-string snuck in.
// Without this, stale bad values from earlier bugs persist forever in
// sessionStorage even after the source bug is fixed.
function isValidNiche(s) {
  if (typeof s !== "string") return false;
  if (s.length === 0 || s.length > 200) return false;
  if (s === "[object Object]") return false;
  if (s.startsWith("[object ")) return false;
  return true;
}

const BLOCKED_MESSAGE =
  "That's outside what PDFTopicScout is built for. Try a niche like personal finance, fitness, parenting, or creative writing.";

const NICHES = [
  "Personal Finance & Investing",
  "Health & Wellness",
  "Digital Marketing",
  "Self-Help & Productivity",
  "Parenting & Family",
  "Business & Entrepreneurship",
  "Technology & AI",
  "Fitness & Nutrition",
  "Real Estate",
  "Online Education & E-Learning",
  "Mental Health & Psychology",
  "Travel & Lifestyle",
  "Food & Cooking",
  "Career & Professional Development",
  "Sustainability & Green Living",
  "Other (type your own)",
];

// Curated subset shown as one-click chips on the empty dashboard. These are
// the highest-converting niches based on early Stripe data.
const QUICK_START_NICHES = [
  "Personal Finance & Investing",
  "Health & Wellness",
  "Digital Marketing",
  "Self-Help & Productivity",
  "Business & Entrepreneurship",
  "Fitness & Nutrition",
  "Mental Health & Psychology",
  "Career & Professional Development",
];

const WEIGHTS = {
  virality: 15, competition: 10, buyerIntent: 15, emotionalPull: 10,
  demographics: 10, urgency: 10, evergreen: 8, specificity: 7,
  monetization: 10, searchDemand: 5,
};

const LOADING_QUOTES = [
  "Your next PDF is about to make someone's wallet lighter 💸",
  "Finding topics people actually want to throw money at...",
  "The algorithm doesn't sleep. Neither should your income.",
  "Your competition is still guessing. You're about to know.",
  "Every PDF sold while you sleep is a win. Let's find yours.",
  "Rich people have multiple income streams. You're building one.",
  "Someone out there needs exactly what you're about to write.",
  "This search could change your financial trajectory. No pressure.",
  "PDFs don't call in sick. Building your 24/7 income machine...",
  "The best time to start was yesterday. Second best? Right now.",
  "Your knowledge is worth more than you think. Let's prove it.",
  "Finding your next sellable idea... the internet never runs out.",
];

// ── Scoring helpers ───────────────────────────────────────────────────────────

function calcSellability(scores) {
  if (!scores) return 65;
  const raw = (
    ((scores.virality || 0) * WEIGHTS.virality +
    (scores.competition || 0) * WEIGHTS.competition +
    (scores.buyerIntent || 0) * WEIGHTS.buyerIntent +
    (scores.emotionalPull || 0) * WEIGHTS.emotionalPull +
    (scores.demographics || 0) * WEIGHTS.demographics +
    (scores.urgency || 0) * WEIGHTS.urgency +
    (scores.evergreen || 0) * WEIGHTS.evergreen +
    (scores.specificity || 0) * WEIGHTS.specificity +
    (scores.monetization || 0) * WEIGHTS.monetization +
    (scores.searchDemand || 0) * WEIGHTS.searchDemand) / 100
  );
  const boosted = raw < 65 ? 65 : 65 + (raw - 65) * 1.4;
  return Math.round(Math.min(98, Math.max(65, boosted)));
}

function scoreColor(s) {
  if (s >= 85) return "#065f46";
  if (s >= 70) return "#0a7c4e";
  if (s >= 65) return "#b45309";
  return "#3b5bdb";
}
function scoreBg(s) {
  if (s >= 85) return "#d1fae5";
  if (s >= 70) return "#ecfdf5";
  if (s >= 65) return "#fffbeb";
  return "#eef1fd";
}
function scoreBorder(s) {
  if (s >= 85) return "#6ee7b7";
  if (s >= 70) return "#a7f3d0";
  if (s >= 65) return "#fde68a";
  return "#c5cff7";
}
function viralityWord(s) {
  if (s >= 80) return "Explosive";
  if (s >= 60) return "High";
  if (s >= 40) return "Moderate";
  return "Low";
}
function competitionWord(s) {
  if (s >= 80) return "Low";
  if (s >= 60) return "Moderate";
  if (s >= 40) return "Competitive";
  return "Saturated";
}
function competitionColor(s) {
  if (s >= 70) return "#0a7c4e";
  if (s >= 50) return "#b45309";
  return "#dc2626";
}
function viralityColor(s) {
  if (s >= 70) return "#3b5bdb";
  if (s >= 50) return "#b45309";
  return "#999";
}

// ── API ───────────────────────────────────────────────────────────────────────

async function callClaude(system, user, model = "claude-sonnet-4-6", maxTokens = 2000) {
  const body = { model, max_tokens: maxTokens, system, messages: [{ role: "user", content: user }] };
  const session = (typeof window !== "undefined" && window.localStorage)
    ? localStorage.getItem("pts_session") || ""
    : "";
  const response = await fetch("/api/claude", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(session ? { Authorization: `Bearer ${session}` } : {}),
    },
    body: JSON.stringify(body),
  });
  if (response.status === 401) {
    // Session missing/expired. Wipe local state and bounce to login.
    try {
      localStorage.removeItem("pts_email");
      localStorage.removeItem("pts_access");
      localStorage.removeItem("pts_session");
    } catch {}
    window.location.href = "/login?error=expired";
    // Throw so callers stop their pipeline. The redirect above will pre-empt.
    throw new Error("Session expired. Please sign in again.");
  }
  if (response.status === 429) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data?.error?.message || "Rate limit hit. Please wait a minute and try again.");
  }
  if (!response.ok) throw new Error("API request failed: " + response.status);
  const data = await response.json();
  if (data.error) throw new Error(data.error.message);
  return data.content.map((b) => (b.type === "text" ? b.text : "")).join("");
}

// ── Loading Screen ────────────────────────────────────────────────────────────

function LoadingScreen() {
  const [quoteIdx, setQuoteIdx] = useState(0);
  const [dots, setDots] = useState(1);
  useEffect(() => {
    const q = setInterval(() => setQuoteIdx(i => (i + 1) % LOADING_QUOTES.length), 5000);
    const d = setInterval(() => setDots(d => d === 3 ? 1 : d + 1), 400);
    return () => { clearInterval(q); clearInterval(d); };
  }, []);
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "60px 20px", textAlign: "center" }}>
      <div style={{ position: "relative", width: 64, height: 64, marginBottom: 28 }}>
        <div style={{ width: 64, height: 64, border: "4px solid #e8e8e4", borderTopColor: "#3b5bdb", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
        <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22 }}>🔍</div>
      </div>
      <div style={{ fontSize: 15, fontWeight: 700, color: "#1a1a1a", marginBottom: 8 }}>
        {"Finding your 10 PDF topics" + ".".repeat(dots)}
      </div>
      <div style={{ fontSize: 13, color: "#999", maxWidth: 340, lineHeight: 1.6, minHeight: 44 }}>
        {LOADING_QUOTES[quoteIdx]}
      </div>
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────

function SellabilityBadge({ score }) {
  const c = scoreColor(score);
  const isHot = score >= 85;
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
      {isHot && (
        <div style={{ background: "#065f46", color: "#fff", fontSize: 9, fontWeight: 700, borderRadius: 20, padding: "2px 8px", letterSpacing: "0.06em", textTransform: "uppercase", whiteSpace: "nowrap" }}>
          🔥 Write This
        </div>
      )}
      <div style={{ display: "inline-flex", flexDirection: "column", alignItems: "center", background: scoreBg(score), border: `1px solid ${scoreBorder(score)}`, borderRadius: 8, padding: "6px 12px", minWidth: 72 }}>
        <span style={{ fontSize: 20, fontWeight: 800, color: c, lineHeight: 1 }}>{score}</span>
        <span style={{ fontSize: 9, color: c, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", marginTop: 2, opacity: 0.8 }}>Sellability</span>
      </div>
    </div>
  );
}

function Pill({ label, value, color }) {
  return (
    <span style={{ display: "inline-flex", gap: 4, alignItems: "center", background: `${color}10`, border: `1px solid ${color}25`, borderRadius: 20, padding: "2px 9px", fontSize: 12 }}>
      <span style={{ color: "#999", fontWeight: 500 }}>{label}:</span>
      <span style={{ color, fontWeight: 600 }}>{value}</span>
    </span>
  );
}

function InsightPanel({ topic, data, onOutline, outlineLoading, outline, onDownloadPDF }) {
  if (!data || !data.scores) return null;
  const { scores, description, targetAudience, viralityDetail, competitionDetail } = data;
  return (
    <div style={{ marginTop: 14, padding: 16, background: "#f7f7f5", borderRadius: 10, border: "1px solid #e8e8e4" }}>
      <p style={{ fontSize: 13, color: "#444", lineHeight: 1.75, marginBottom: 14 }}>{description}</p>
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 10, color: "#999", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 4 }}>Target Audience</div>
        <div style={{ fontSize: 13, color: "#333", fontWeight: 500 }}>{targetAudience}</div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 14 }}>
        {[
          { label: "Virality", value: viralityWord(scores.virality), color: viralityColor(scores.virality), detail: viralityDetail },
          { label: "Competition", value: competitionWord(scores.competition), color: competitionColor(scores.competition), detail: competitionDetail },
          { label: "Market", value: (targetAudience || "").split(",")[0].trim(), color: "#3b5bdb", detail: "Primary buyer group" },
        ].map(({ label, value, color, detail }) => (
          <div key={label} style={{ background: "#fff", borderRadius: 8, padding: "10px 12px", border: "1px solid #e8e8e4" }}>
            <div style={{ fontSize: 9, color: "#aaa", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 4 }}>{label}</div>
            <div style={{ fontSize: 14, fontWeight: 700, color, marginBottom: 4 }}>{value}</div>
            <div style={{ fontSize: 11, color: "#999", lineHeight: 1.4 }}>{detail}</div>
          </div>
        ))}
      </div>
      <button onClick={onOutline} disabled={outlineLoading} style={{
        width: "100%",
        // Keep the brand-blue when loading rather than greying out — the
        // user reads disabled+grey as "broken." A slightly muted blue plus a
        // subtle pulse animation reads as "working, just be patient."
        background: outlineLoading ? "#7a8fde" : "#3b5bdb",
        color: "#fff", border: "none", borderRadius: 8,
        padding: 9, fontWeight: 600, fontSize: 13,
        cursor: outlineLoading ? "wait" : "pointer",
        marginBottom: outline ? 12 : 0, fontFamily: "Inter, sans-serif",
        animation: outlineLoading ? "ptsPulse 1.4s ease-in-out infinite" : "none",
        transition: "background 0.2s ease",
      }}>
        {outlineLoading
          ? "⏳ Your outline is generating — takes 10-20s"
          : outline ? "↻ Regenerate Outline" : "📝 Generate Outline"}
      </button>
      {outline && (
        <div style={{ background: "#fff", borderRadius: 8, padding: "14px 16px", border: "1px solid #e8e8e4" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10, gap: 8, flexWrap: "wrap" }}>
            <div style={{ fontSize: 10, color: "#3b5bdb", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" }}>Chapter Outline</div>
            <button
              onClick={() => onDownloadPDF(topic, outline)}
              style={{
                background: "#fff", border: "1px solid #3b5bdb", color: "#3b5bdb",
                borderRadius: 6, padding: "5px 12px", fontSize: 12, fontWeight: 600,
                cursor: "pointer", fontFamily: "Inter, sans-serif",
              }}
            >
              ⬇ Download as PDF
            </button>
          </div>
          <div style={{ fontSize: 13, color: "#333", lineHeight: 1.8, whiteSpace: "pre-wrap" }}>{outline}</div>
        </div>
      )}
    </div>
  );
}

function ScoreBar({ score }) {
  const color = scoreColor(score);
  const label = score >= 85 ? "Excellent sellability" : score >= 75 ? "Strong sellability" : score >= 65 ? "Good sellability" : "Moderate sellability";
  return (
    <div style={{ marginTop: 10, marginBottom: 2 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 5 }}>
        <span style={{ fontSize: 10, color: "#999", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em" }}>Sellability strength</span>
        <span style={{ fontSize: 10, color, fontWeight: 700 }}>{label}</span>
      </div>
      <div style={{ height: 5, background: "#f0f0ee", borderRadius: 3, overflow: "hidden" }}>
        <div style={{ height: "100%", width: `${score}%`, background: color, borderRadius: 3, transition: "width 0.8s ease" }} />
      </div>
    </div>
  );
}

function TopicCard({ topic, isFav, onFav, insightData, onToggleInsight, outlineLoading, outline, onOutline, onDownloadPDF }) {
  const hasScores = insightData && insightData.scores && !insightData.loading && !insightData.error;
  const isScoring = insightData && insightData.loading;
  const sell = hasScores ? calcSellability(insightData.scores) : null;
  return (
    <div style={{ background: "#fff", border: "1px solid #e8e8e4", borderRadius: 10, padding: "16px 18px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: "#1a1a1a", lineHeight: 1.5, marginBottom: hasScores ? 10 : 6 }}>{topic}</div>
          {hasScores && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              <Pill label="Virality" value={viralityWord(insightData.scores.virality)} color={viralityColor(insightData.scores.virality)} />
              <Pill label="Competition" value={competitionWord(insightData.scores.competition)} color={competitionColor(insightData.scores.competition)} />
              <Pill label="Audience" value={(insightData.targetAudience || "").split(",")[0].trim()} color="#3b5bdb" />
            </div>
          )}
          {/* Scoring in progress */}
          {isScoring && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4 }}>
              <div style={{ width: 12, height: 12, border: "2px solid #e0e0dc", borderTopColor: "#3b5bdb", borderRadius: "50%", animation: "spin 0.8s linear infinite", flexShrink: 0 }} />
              <span style={{ fontSize: 12, color: "#aaa" }}>Calculating score…</span>
            </div>
          )}
          {/* Teaser before scores load */}
          {!hasScores && !isScoring && (
            <div style={{ fontSize: 12, color: "#bbb", fontStyle: "italic" }}>Click Show insight for full market analysis →</div>
          )}
        </div>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 8, flexShrink: 0 }}>
          {hasScores && <SellabilityBadge score={sell} />}
          {insightData && insightData.loading && (
            <div style={{ width: 72, height: 52, background: "#f0f0ee", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <div style={{ width: 16, height: 16, border: "2px solid #e0e0dc", borderTopColor: "#3b5bdb", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
            </div>
          )}
          <button onClick={() => onFav(topic)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18, color: isFav ? "#e8590c" : "#ccc", padding: 0 }}>
            {isFav ? "★" : "☆"}
          </button>
        </div>
      </div>
      {hasScores && <ScoreBar score={sell} />}
      <div style={{ marginTop: 10 }}>
        <button onClick={() => !insightData?.loading && onToggleInsight(topic)} style={{
          background: "transparent", border: "1px solid #e8e8e4",
          color: insightData?.loading ? "#aaa" : "#666",
          borderRadius: 6, padding: "5px 12px",
          cursor: insightData?.loading ? "not-allowed" : "pointer",
          fontSize: 12, fontWeight: 500, fontFamily: "Inter, sans-serif",
        }}>
          {insightData?.loading ? "Analysing…" : insightData?.visible ? "Hide insight" : "Show insight"}
        </button>
      </div>
      {insightData?.visible && insightData?.error && (
        <div style={{ marginTop: 10, padding: "10px 14px", background: "#fef2f2", borderRadius: 8, border: "1px solid #fecaca", fontSize: 13, color: "#dc2626" }}>
          {["Our scouts got lost in the niche — click Show insight to retry!",
            "The algorithm took a coffee break. Click Show insight to retry!",
            "We went to Mars for this score and lost signal. Retry?",
            "Our AI tripped over a semicolon. Click Show insight to retry!",
          ][Math.abs(topic.length % 4)]}
        </div>
      )}
      {insightData?.visible && hasScores && (
        <InsightPanel topic={topic} data={insightData} onOutline={onOutline} outlineLoading={outlineLoading} outline={outline} onDownloadPDF={onDownloadPDF} />
      )}
    </div>
  );
}

// ── Main App ──────────────────────────────────────────────────────────────────

export default function App({ onLogout, onHome, userEmail, userName }) {
  const [niche, setNiche] = useState("");
  const [custom, setCustom] = useState("");
  const [search, setSearch] = useState("");
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [favs, setFavs] = useState([]);
  const [showFavs, setShowFavs] = useState(false);
  const [insights, setInsights] = useState({});
  const [outlines, setOutlines] = useState({});
  const [outlineLoading, setOutlineLoading] = useState({});
  // Usage/tier state. `balance` is "unlimited" (string) for founder_lifetime,
  // otherwise a number. `unlimited` is the easy boolean to gate UI on.
  const [usage, setUsage] = useState({
    balance: 0,
    tier: null,
    tierLabel: "",
    badge: null,
    unlimited: false,
    priority: false,
    canHideLowSellability: false,
  });
  // Pro/Founder Lifetime toggle: only show topics with sellability ≥ 80.
  const [hideLow, setHideLow] = useState(false);
  // Top-up modal — opened by "Top up credits" buttons. Renders a small
  // dialog with the two pack options + an "Upgrade to Pro" button for
  // Standard users.
  const [topUpOpen, setTopUpOpen] = useState(false);
  const [topUpLoading, setTopUpLoading] = useState(null); // "pack_50" | "pack_150" | "upgrade" | null
  // Boards (Pro/Founder feature) — organize topics into named project buckets.
  // `boards` is the user's list. `activeBoardId` non-null means viewing the
  // contents of that board (otherwise the boards list / topics list shows).
  // `addToBoardFor` holds the topic title when the "Add to board" picker is
  // open for a specific topic; null when closed.
  const [boards, setBoards] = useState([]);
  const [boardsView, setBoardsView] = useState(false); // are we showing boards instead of topics?
  const [activeBoardId, setActiveBoardId] = useState(null);
  const [activeBoardTopics, setActiveBoardTopics] = useState([]);
  const [addToBoardFor, setAddToBoardFor] = useState(null); // topic string or null
  const [boardLoading, setBoardLoading] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const cache = useRef({});
  // Map of normalized niche → array of topic strings recently shown for that
  // niche. Used to push Claude toward variety when the same niche is searched
  // multiple times in a session. Kept in-memory only; resets on reload, which
  // is fine — variety only matters within a single user session.
  const recentTopics = useRef({});
  const menuRef = useRef(null);

  // Close mobile menu when clicking outside
  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", handler);
    document.addEventListener("touchstart", handler);
    return () => {
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("touchstart", handler);
    };
  }, [menuOpen]);

  // Close the "Add to board" picker on any click outside a picker element.
  // We mark each picker wrapper with `.pts-board-picker`; the handler walks
  // up the DOM tree from the click target and closes the picker only when
  // no ancestor has that class.
  useEffect(() => {
    if (!addToBoardFor) return;
    const handler = (e) => {
      let node = e.target;
      while (node && node !== document.body) {
        if (node.classList && node.classList.contains("pts-board-picker")) return;
        node = node.parentElement;
      }
      setAddToBoardFor(null);
    };
    document.addEventListener("mousedown", handler);
    document.addEventListener("touchstart", handler);
    return () => {
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("touchstart", handler);
    };
  }, [addToBoardFor]);

  // Restore last search from sessionStorage
  useEffect(() => {
    try {
      const saved = sessionStorage.getItem("pts_last_topics");
      const savedNiche = sessionStorage.getItem("pts_last_niche");
      const savedInsights = sessionStorage.getItem("pts_last_insights");
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) {
          setTopics(parsed);
          // Only restore the search field if the saved value is actually a
          // legitimate niche string. If we saved a bad value (like
          // "[object Object]") from a previous version of the app, drop it
          // silently so the user sees a clean empty search field.
          if (isValidNiche(savedNiche)) setSearch(savedNiche);
          else if (savedNiche) sessionStorage.removeItem("pts_last_niche");
        }
      }
      if (savedInsights) {
        const parsedInsights = JSON.parse(savedInsights);
        setInsights(parsedInsights);
        Object.entries(parsedInsights).forEach(([topic, data]) => {
          if (data && data.scores) cache.current[topic] = data;
        });
      }
    } catch {}
  }, []);

  // Load usage and favorites on mount
  useEffect(() => {
    if (userEmail) {
      fetch(`/api/credits?email=${encodeURIComponent(userEmail)}`)
        .then(r => r.json())
        .then(data => {
          if (data.tier) setUsage(data);
          // Boards are a Pro/Founder Lifetime feature — only fetch them for
          // tiers that have access. Saves an API call for Standard/Trial users.
          if (data.tier === "pro" || data.tier === "founder_lifetime") {
            fetch(`/api/boards?email=${encodeURIComponent(userEmail)}`)
              .then(r => r.json())
              .then(bd => { if (Array.isArray(bd.boards)) setBoards(bd.boards); })
              .catch(() => {});
          }
        })
        .catch(() => {});
      fetch(`/api/favorites?email=${encodeURIComponent(userEmail)}`)
        .then(r => r.json())
        .then(data => { if (Array.isArray(data.favs)) setFavs(data.favs); })
        .catch(() => {});
    }
  }, [userEmail]);

  const fetchTopics = async (overrideQuery = null) => {
    // Belt-and-suspenders: only honor overrideQuery if it's actually a valid
    // string. This guards against the historical footgun where a click event
    // got passed in by mistake (e.g. <button onClick={fetchTopics}>).
    const safeOverride = isValidNiche(overrideQuery) ? overrideQuery : null;
    const q = safeOverride || search.trim() || (niche === "Other (type your own)" ? custom : niche);
    if (!q || !isValidNiche(q)) { setError("Pick a niche or type a topic first."); return; }

    // Safety blocklist — refuse obviously harmful queries before spending any
    // API budget or a usage-quota slot. Cheap regex, instant feedback. The
    // server has the same list as defense in depth.
    if (isHarmfulQuery(q)) {
      console.log("[pts] blocked by safety filter:", q);
      setError(BLOCKED_MESSAGE);
      return;
    }

    setLoading(true); setError(""); setTopics([]); setInsights({}); setOutlines({});
    cache.current = {};
    try {
      sessionStorage.removeItem("pts_last_topics");
      sessionStorage.removeItem("pts_last_insights");
      sessionStorage.removeItem("pts_last_niche");
    } catch {}

    // Charge 1 credit for the search (founder_lifetime is unlimited so this is
    // free for legacy users). Reject early if the balance can't cover it.
    try {
      const usageRes = await fetch("/api/credits", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: userEmail, amount: 1 }),
      });
      const usageData = await usageRes.json();
      if (usageRes.status === 429) {
        setError(`Out of credits. You have ${usageData.balance} left on your ${usageData.tierLabel} plan — top up or upgrade to keep searching.`);
        setTopUpOpen(true);
        setLoading(false);
        return;
      }
      if (usageRes.status === 402) {
        setError("No active plan found. Please sign in again.");
        setLoading(false);
        return;
      }
      if (usageData && usageData.tier) setUsage(usageData);
    } catch {}

    try {
      // Build the "do not repeat" exclusion line if we've shown topics for
      // this niche before in this session. Without this, repeat searches of
      // the same niche surface heavily-overlapping titles because the same
      // training-data patterns get pulled. The clause nudges Claude toward
      // genuinely different angles. Cap at 30 strings to keep prompt size in
      // check — past that, Claude starts ignoring the exclusion anyway.
      const nicheKey = String(q).toLowerCase().trim();
      const previous = recentTopics.current[nicheKey] || [];
      const excludeList = previous.slice(-30);
      const exclusionClause = excludeList.length > 0
        ? `\n\nIMPORTANT: do NOT generate any of these titles or close paraphrases of them ` +
          `(already shown to this user recently): ${excludeList.map(t => `"${t}"`).join(", ")}. ` +
          `Pick genuinely different angles.`
        : "";

      // Single Claude-only generation path. The prompt is the tightened
      // version that explicitly forbids metaphor/wordplay and demands
      // directly-relevant titles — the rewrite that killed the old
      // "Burner Account Psychology for Food & Cooking" failure mode.
      const system =
        "You are a content research expert generating PDF topic ideas for content creators. " +
        "Return ONLY a JSON array of exactly 10 PDF topic strings. " +
        "EVERY topic must be directly and obviously about the niche specified — " +
        "no metaphors, no wordplay, no tangential interpretations. " +
        "If the niche is 'Food & Cooking', topics must be about food and cooking " +
        "(recipes, meal prep, cooking techniques), NOT about 'burner accounts' or 'controlled burns'. " +
        "Each title 8-14 words, specific and benefit-focused. Write titles that mirror real " +
        "Google search patterns — what someone with a problem actually types. " +
        "Avoid AI clichés: never use delve, leverage, comprehensive, unlock, navigate, " +
        "transformative, paradigm, holistic, synergy, journey, embark, dive into, dynamic landscape. " +
        "If the niche is harmful/illegal (weapons, drug synthesis, suicide methods, " +
        "child exploitation, real-world violence), return exactly [\"BLOCKED\"]. " +
        "No numbering, no markdown.";
      const user =
        `Niche: ${q}\n\nGenerate 10 PDF topic ideas that someone searching for "${q}" PDFs ` +
        `would actually want to buy. Variation: ${Math.random().toString(36).substring(2, 8)}.` +
        exclusionClause;
      const namesText = await callClaude(system, user);

      const namesJson = namesText.replace(/```json|```/g, "").trim();
      const namesStart = namesJson.indexOf("[");
      const namesEnd = namesJson.lastIndexOf("]");
      if (namesStart === -1 || namesEnd === -1) throw new Error("parse_error");
      let newTopics;
      try { newTopics = JSON.parse(namesJson.slice(namesStart, namesEnd + 1)); }
      catch { throw new Error("parse_error"); }
      if (!Array.isArray(newTopics) || newTopics.length === 0) throw new Error("parse_error");

      // Check for the safety BLOCKED sentinel before showing anything to the user
      const looksBlocked =
        newTopics.length === 1 &&
        typeof newTopics[0] === "string" &&
        newTopics[0].trim().toUpperCase() === "BLOCKED";
      if (looksBlocked) {
        console.log("[pts] Claude returned BLOCKED for:", q);
        setError(BLOCKED_MESSAGE);
        setLoading(false);
        return;
      }

      newTopics = newTopics.slice(0, 10);
      setTopics(newTopics);
      // Remember what we just showed for this niche, so a repeat search nudges
      // Claude toward different angles next time.
      const existing = recentTopics.current[nicheKey] || [];
      recentTopics.current[nicheKey] = [...existing, ...newTopics].slice(-30);
      try {
        sessionStorage.setItem("pts_last_topics", JSON.stringify(newTopics));
        sessionStorage.setItem("pts_last_niche", q);
      } catch {}
      setLoading(false);
      scorePageTopics(newTopics);
    } catch (e) {
      const funErrors = [
        "We went to Mars to find your PDF topics and lost signal. Try again!",
        "Our topic scouts got distracted by their own PDFs. One more try?",
        "The algorithm took a coffee break. Give it another shot!",
        "Houston, we have a problem. But it's temporary — try again!",
        "Our AI tripped over a semicolon. It's back up, try again!",
      ];
      const msg = e.message === "parse_error"
        ? funErrors[Math.floor(Math.random() * funErrors.length)]
        : "Something went wrong. Please try again!";
      setError(msg);
      setLoading(false);
    }
  };

  const scorePageTopics = async (topicsToScore) => {
    const unscored = topicsToScore.filter(t => !cache.current[t]);
    if (unscored.length === 0) return;
    const system = "You are a PDF market analyst. Score each topic honestly. These are pre-selected good topics so sellability scores should all be 65+. Individual dimension scores (virality, competition etc) can vary widely 20-100 to reflect real market conditions. Return ONLY a JSON array. Each item: {topic, description (2-3 casual punchy human sentences), targetAudience (2-3 groups comma separated), viralityDetail (1 sentence), competitionDetail (1 sentence), scores:{virality,competition,buyerIntent,emotionalPull,demographics,urgency,evergreen,specificity,monetization,searchDemand}}. competition 100=very low competition. No markdown.";
    setInsights(p => {
      const next = { ...p };
      unscored.forEach(t => { next[t] = { loading: true, visible: false }; });
      return next;
    });
    const mid = Math.ceil(unscored.length / 2);
    const batch1 = unscored.slice(0, mid);
    const batch2 = unscored.slice(mid);
    const scoreBatch = async (batch) => {
      try {
        const text = await callClaude(system, "Score these PDF topics: " + JSON.stringify(batch));
        const jsonText = text.replace(/```json|```/g, "").trim();
        const start = jsonText.indexOf("[");
        const end = jsonText.lastIndexOf("]");
        if (start === -1 || end === -1) return [];
        const scored = JSON.parse(jsonText.slice(start, end + 1));
        return Array.isArray(scored) ? scored : [];
      } catch { return []; }
    };
    const [results1, results2] = await Promise.all([scoreBatch(batch1), scoreBatch(batch2)]);
    const allScored = [...results1, ...results2];
    setInsights(p => {
      const next = { ...p };
      unscored.forEach(t => {
        const found = allScored.find(s => s.topic === t);
        if (!found) next[t] = { error: true, loading: false, visible: false };
      });
      allScored.forEach(item => {
        if (item.topic && item.scores) {
          cache.current[item.topic] = item;
          next[item.topic] = { ...item, loading: false, visible: false };
        }
      });
      try { sessionStorage.setItem("pts_last_insights", JSON.stringify(next)); } catch {}
      return next;
    });
  };

  const handleToggleInsight = async (topic) => {
    const cur = insights[topic];
    if (cur && !cur.loading && !cur.error) {
      setInsights(p => ({ ...p, [topic]: { ...p[topic], visible: !p[topic].visible } }));
      return;
    }
    if (cache.current[topic] && cache.current[topic].scores) {
      setInsights(p => ({ ...p, [topic]: { ...cache.current[topic], visible: true, loading: false } }));
      return;
    }
    // Fallback — fetch individually
    setInsights(p => ({ ...p, [topic]: { loading: true, visible: true } }));
    try {
      const fallbackSystem = "You are a PDF market analyst. Return ONLY a valid JSON object with keys: description, targetAudience, viralityDetail, competitionDetail, scores (virality,competition,buyerIntent,emotionalPull,demographics,urgency,evergreen,specificity,monetization,searchDemand). All scores 0-100, competition 100=very low. No markdown.";
      const text = await callClaude(fallbackSystem, "Score this PDF topic: " + topic);
      const jsonText = text.replace(/```json|```/g, "").trim();
      const s = jsonText.indexOf("{"); const e = jsonText.lastIndexOf("}");
      if (s === -1 || e === -1) throw new Error("bad json");
      const data = JSON.parse(jsonText.slice(s, e + 1));
      cache.current[topic] = data;
      setInsights(p => {
        const next = { ...p, [topic]: { ...data, loading: false, visible: true } };
        try { sessionStorage.setItem("pts_last_insights", JSON.stringify(next)); } catch {}
        return next;
      });
    } catch {
      setInsights(p => ({ ...p, [topic]: { error: true, loading: false, visible: true } }));
    }
  };

  const handleOutline = async (topic) => {
    setOutlineLoading(p => ({ ...p, [topic]: true }));
    try {
      // Charge 0.5 credits for the outline action. Founder Lifetime users
      // are unlimited so this is a no-op for them. Standard/Pro users pay.
      const cRes = await fetch("/api/credits", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: userEmail, amount: 0.5 }),
      });
      const cData = await cRes.json();
      if (cRes.status === 429) {
        setOutlines(p => ({ ...p, [topic]: `Out of credits. You need 0.5 to generate an outline but only have ${cData.balance}.` }));
        setTopUpOpen(true);
        setOutlineLoading(p => ({ ...p, [topic]: false }));
        return;
      }
      if (cData && cData.tier) setUsage(cData);
      // Tight outline prompt. Constraints in order of importance:
      //   - Exactly 6 chapters (not "8-10" — that drifts long)
      //   - Each chapter: 1-line title + 2-3 short bullets (<12 words each)
      //   - Total under 350 words (max_tokens=800 enforces an upper bound)
      //   - Banned-word list to kill AI clichés that creep in by default
      //   - No intro/conclusion fluff — readers can write their own
      //   - Plain text (no asterisks, hashes, dashes for bullets — just letters)
      const system =
        "You write tight, scannable PDF outlines for content creators. " +
        "Generate EXACTLY 6 chapters. Each chapter has: a one-line title (under 12 words), " +
        "then 2-3 short bullet sub-points, each under 12 words. Total output under 350 words. " +
        "Plain text only — no markdown symbols (no asterisks, no hashes, no dashes for bullets, " +
        "use 'bullet text' on its own line). " +
        "Forbidden words (never use these): delve, leverage, comprehensive, robust, unlock, " +
        "navigate, transformative, holistic, synergy, paradigm, journey, embark, dive into, " +
        "in today's, dynamic landscape, cutting-edge, seamless, revolutionize, empower, " +
        "harness, ecosystem. " +
        "No introduction. No conclusion. No 'in this chapter we will'. Just six chapters " +
        "with titles and bullets, written like a pro editor who hates fluff. " +
        "Format each chapter as: 'Chapter N: Title' on one line, then bullets on their own " +
        "lines below (one per line), then a blank line before the next chapter.";

      const user = `Write a tight, practical 6-chapter PDF outline for: "${topic}"`;

      const text = await callClaude(system, user, "claude-sonnet-4-6", 800);
      setOutlines(p => ({ ...p, [topic]: text }));
    } catch {
      setOutlines(p => ({ ...p, [topic]: "Couldn't generate outline — try again." }));
    }
    setOutlineLoading(p => ({ ...p, [topic]: false }));
  };

  const toggleFav = (topic) => {
    setFavs(p => {
      const next = p.includes(topic) ? p.filter(t => t !== topic) : [...p, topic];
      fetch("/api/favorites", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: userEmail, favs: next }),
      }).catch(() => {});
      return next;
    });
  };

  const exportFavs = () => {
    const rows = [["Topic", "Sellability Score", "Virality", "Competition", "Target Audience"]];
    favs.forEach(t => {
      const d = cache.current[t];
      if (d && d.scores) {
        rows.push([t, calcSellability(d.scores), viralityWord(d.scores.virality), competitionWord(d.scores.competition), d.targetAudience || "—"]);
      } else {
        rows.push([t, "Not analysed yet", "—", "—", "—"]);
      }
    });
    const csv = rows.map(r => r.map(c => '"' + String(c).replace(/"/g, '""') + '"').join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "pdftopicscout-favorites.csv";
    document.body.appendChild(a); a.click();
    document.body.removeChild(a); URL.revokeObjectURL(url);
  };

  // Buy a credit pack. Routes through Stripe checkout — same flow as the
  // pricing page subscribe buttons, just with a pack productType. Stripe
  // redirects back to /app on success, and the webhook adds the credits to
  // the user's pack bucket before the next page load.
  const handleBuyPack = async (productType) => {
    setTopUpLoading(productType);
    try {
      const res = await fetch("/api/create-checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ productType, email: userEmail }),
      });
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
        return;
      }
      alert(data.error || "Could not start checkout. Try again in a moment.");
    } catch {
      alert("Could not start checkout. Try again in a moment.");
    }
    setTopUpLoading(null);
  };

  // Mid-cycle Standard → Pro upgrade. Stripe handles proration; backend
  // proactively pins the new tier in Upstash so the UI updates immediately.
  const handleUpgradeToPro = async () => {
    setTopUpLoading("upgrade");
    try {
      const res = await fetch("/api/upgrade-plan", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: userEmail, target: "sub_pro" }),
      });
      const data = await res.json();
      if (data.success) {
        // Refresh the usage state so the badge flips to Pro instantly
        const cRes = await fetch(`/api/credits?email=${encodeURIComponent(userEmail)}`);
        const cData = await cRes.json();
        if (cData.tier) setUsage(cData);
        setTopUpOpen(false);
        alert("Welcome to Pro! Your credits have been upgraded to 100 per month.");
      } else {
        alert(data.error || "Could not upgrade. Please try again.");
      }
    } catch {
      alert("Could not upgrade. Please try again.");
    }
    setTopUpLoading(null);
  };

  // ── Boards (Pro/Founder feature) ─────────────────────────────────────────

  // Load the user's boards list from the backend, with topic counts. Called
  // on tier load (if user is Pro/Founder) and after any mutation.
  const fetchBoards = async () => {
    if (!userEmail) return;
    try {
      const res = await fetch(`/api/boards?email=${encodeURIComponent(userEmail)}`);
      const data = await res.json();
      if (Array.isArray(data.boards)) setBoards(data.boards);
    } catch {}
  };

  const createBoard = async () => {
    const name = window.prompt("Board name (e.g. 'Q1 Etsy PDFs')");
    if (!name || !name.trim()) return;
    setBoardLoading(true);
    try {
      const res = await fetch("/api/boards", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: userEmail, action: "create", name: name.trim() }),
      });
      const data = await res.json();
      if (data.board) {
        await fetchBoards();
      } else {
        alert(data.error || "Could not create board.");
      }
    } catch {
      alert("Could not create board.");
    }
    setBoardLoading(false);
  };

  const deleteBoard = async (id, name) => {
    if (!window.confirm(`Delete board "${name}"? Topics inside will be lost.`)) return;
    setBoardLoading(true);
    try {
      await fetch("/api/boards", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: userEmail, action: "delete", id }),
      });
      await fetchBoards();
      // If we were viewing the deleted board, go back to the list
      if (activeBoardId === id) {
        setActiveBoardId(null);
        setActiveBoardTopics([]);
      }
    } catch {}
    setBoardLoading(false);
  };

  // Open a specific board, fetching its topics
  const openBoard = async (id) => {
    setBoardLoading(true);
    setActiveBoardId(id);
    try {
      const res = await fetch("/api/boards", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: userEmail, action: "list_topics", board_id: id }),
      });
      const data = await res.json();
      setActiveBoardTopics(Array.isArray(data.topics) ? data.topics : []);
    } catch {}
    setBoardLoading(false);
  };

  // Add a topic to a board from the "Add to board" picker
  const addTopicToBoard = async (boardId, topic) => {
    try {
      await fetch("/api/boards", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: userEmail, action: "add_topic", board_id: boardId, topic }),
      });
      await fetchBoards();
    } catch {}
    setAddToBoardFor(null);
  };

  // Remove a topic from a board (only relevant in board view)
  const removeTopicFromBoard = async (topic) => {
    if (!activeBoardId) return;
    try {
      const res = await fetch("/api/boards", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: userEmail, action: "remove_topic", board_id: activeBoardId, topic }),
      });
      const data = await res.json();
      setActiveBoardTopics(Array.isArray(data.topics) ? data.topics : activeBoardTopics.filter(t => t !== topic));
      await fetchBoards(); // refresh counts
    } catch {}
  };

  // Render a generated outline to a downloadable PDF using jsPDF. Format is
  // intentionally minimal: title, subtitle, body text wrapped to the page
  // width, page numbers in the footer. The user is supposed to take this and
  // expand it in Canva/Word/Notion — it's a starting skeleton, not a final
  // product, so heavy design here would be wasted work.
  const downloadOutlineAsPDF = (topic, outline) => {
    if (!outline) return;
    const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: "letter" });
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const margin = 60;
    const contentW = pageW - margin * 2;

    // Title
    doc.setFont("helvetica", "bold");
    doc.setFontSize(18);
    doc.setTextColor(26, 26, 26);
    const titleLines = doc.splitTextToSize(topic, contentW);
    doc.text(titleLines, margin, margin + 8);

    // Subtitle
    let y = margin + 8 + titleLines.length * 22 + 6;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(120, 120, 120);
    doc.text("PDF Outline · Generated by PDFTopicScout", margin, y);
    y += 22;

    // Body
    doc.setTextColor(40, 40, 40);
    doc.setFontSize(11);
    const bodyLines = doc.splitTextToSize(outline, contentW);
    for (const line of bodyLines) {
      if (y > pageH - margin - 24) {
        doc.addPage();
        y = margin;
      }
      // Slight bold for lines that look like chapter headers
      const isHeader = /^chapter\s+\d+/i.test(line.trim());
      if (isHeader) {
        doc.setFont("helvetica", "bold");
        if (y > margin + 8) y += 6; // breathing room before next chapter
      } else {
        doc.setFont("helvetica", "normal");
      }
      doc.text(line, margin, y);
      y += 16;
    }

    // Page-number footer on every page
    const pages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pages; i++) {
      doc.setPage(i);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(160, 160, 160);
      doc.text(
        `pdftopicscout.creatovate.biz · page ${i} of ${pages}`,
        pageW / 2,
        pageH - 28,
        { align: "center" },
      );
    }

    // Sanitize the topic to make a filename
    const filename = topic
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .substring(0, 60) + "-outline.pdf";

    doc.save(filename || "outline.pdf");
  };


  const baseList = showFavs ? favs : topics;
  // When the "Hide low-sellability" Pro toggle is on, drop topics scoring
  // below 80. Topics that haven't been scored yet (insightData missing or
  // still loading) always pass through — we don't want them to disappear
  // mid-scoring and reappear when the score lands.
  const list = (hideLow && usage.canHideLowSellability)
    ? baseList.filter(t => {
        const d = insights[t];
        if (!d || !d.scores) return true;
        return calcSellability(d.scores) >= 80;
      })
    : baseList;
  const activeNiche = niche === "Other (type your own)" ? custom : niche;

  return (
    <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", background: "#f7f7f5", fontFamily: "Inter, sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes ptsPulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.7; } }
        @keyframes slideDown { from { opacity: 0; transform: translateY(-8px); } to { opacity: 1; transform: translateY(0); } }
        input:focus, select:focus { outline: none; border-color: #3b5bdb !important; box-shadow: 0 0 0 3px rgba(59,91,219,0.08); }

        /* ── Responsive nav ────────────────────────────────────────────────── */
        .pts-header { padding: 16px 28px; }
        .pts-desktop-nav { display: flex; gap: 8px; align-items: center; }
        .pts-mobile-toggle { display: none; }
        .pts-greeting { display: inline; }
        .pts-subtitle { display: inline; }

        @media (max-width: 640px) {
          .pts-header { padding: 12px 16px; }
          .pts-desktop-nav { display: none !important; }
          .pts-mobile-toggle { display: flex !important; }
          .pts-greeting { display: none; }
          .pts-subtitle { display: none; }
          .pts-body { padding: 18px 14px !important; }
          .pts-search-panel { padding: 16px !important; }
          .pts-search-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>

      {/* Header */}
      <div className="pts-header" style={{ borderBottom: "1px solid #e8e8e4", background: "#fff", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, position: "sticky", top: 0, zIndex: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
          <button onClick={() => { setShowFavs(false); }} style={{ background: "none", border: "none", cursor: "pointer", padding: 0, display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 17, fontWeight: 800, color: "#1a1a1a", letterSpacing: "-0.3px" }}>PDFTopicScout</span>
            <span className="pts-subtitle" style={{ fontSize: 12, color: "#3b5bdb", fontWeight: 600 }}>by Creatovate</span>
          </button>
          {userName && (
            <span className="pts-greeting" style={{ fontSize: 13, color: "#999", fontWeight: 500 }}>
              Hey, <strong style={{ color: "#1a1a1a" }}>{userName}</strong> 👋
            </span>
          )}
        </div>

        {/* Desktop button row */}
        <div className="pts-desktop-nav">
          <button onClick={() => { window.location.href = "/guide"; }} style={{
            background: "transparent", border: "none", color: "#555",
            cursor: "pointer", fontWeight: 600, fontSize: 13, fontFamily: "Inter, sans-serif",
            padding: "7px 10px", borderRadius: 8,
          }}>
            Guide
          </button>
          {(() => {
            // Compute display values for the credit/tier badge. Unlimited
            // users (founder_lifetime) get a special always-green ★ badge.
            // Numeric users get color thresholds based on % of their monthly
            // allotment remaining so the badge means the same thing for
            // Standard (50/mo) and Pro (100/mo).
            const monthly = typeof usage.monthlyCredits === "number" ? usage.monthlyCredits : 50;
            const pct = usage.unlimited ? 100 : (typeof usage.balance === "number" ? (usage.balance / monthly) * 100 : 100);
            const color = usage.unlimited ? "#0a7c4e" : (pct <= 20 ? "#dc2626" : pct <= 50 ? "#b45309" : "#64748b");
            const bg = usage.unlimited ? "#ecfdf5" : (pct <= 20 ? "#fef2f2" : pct <= 50 ? "#fffbeb" : "#f7f7f5");
            const border = usage.unlimited ? "#a7f3d0" : (pct <= 20 ? "#fecaca" : pct <= 50 ? "#fde68a" : "#e8e8e4");
            return (
              <span style={{
                fontSize: 12, fontWeight: 600, color, background: bg,
                border: `1px solid ${border}`, borderRadius: 20, padding: "3px 10px",
                display: "inline-flex", alignItems: "center", gap: 6,
              }}>
                {usage.badge && <span>{usage.badge}</span>}
                {usage.unlimited
                  ? `${usage.tierLabel || "Unlimited"} · ∞ credits`
                  : `${usage.balance} credits · ${usage.tierLabel || ""}`}
                {!usage.unlimited && usage.tier && (
                  <button
                    onClick={() => setTopUpOpen(true)}
                    title="Top up credits or upgrade plan"
                    style={{
                      background: "transparent", border: "none",
                      color: "#3b5bdb", fontSize: 11, fontWeight: 700,
                      cursor: "pointer", padding: "0 0 0 4px",
                      fontFamily: "Inter, sans-serif",
                      borderLeft: `1px solid ${border}`,
                      marginLeft: 2,
                    }}
                  >
                    + Top up
                  </button>
                )}
              </span>
            );
          })()}
          {showFavs && favs.length > 0 && (
            <button onClick={exportFavs} style={{ background: "transparent", border: "1px solid #e8e8e4", color: "#666", borderRadius: 8, padding: "7px 14px", cursor: "pointer", fontWeight: 500, fontSize: 12, fontFamily: "Inter, sans-serif" }}>
              ⬇ Export CSV
            </button>
          )}
          <button onClick={() => { setShowFavs(v => !v); if (!showFavs) { setBoardsView(false); setActiveBoardId(null); } }} style={{
            background: showFavs ? "#eef1fd" : "transparent",
            border: `1px solid ${showFavs ? "#3b5bdb" : "#e8e8e4"}`,
            color: showFavs ? "#3b5bdb" : "#666",
            borderRadius: 8, padding: "7px 16px", cursor: "pointer", fontWeight: 600, fontSize: 13, fontFamily: "Inter, sans-serif",
          }}>
            ★ Saved {favs.length > 0 && `(${favs.length})`}
          </button>
          {/* Boards — Pro/Founder Lifetime only. Reuses the canHideLowSellability
              flag as the "is this a Pro tier" check since it's set on the same
              tiers and saves us threading another flag through the API. */}
          {usage.canHideLowSellability && (
            <button onClick={() => { setBoardsView(v => !v); if (!boardsView) { setShowFavs(false); setActiveBoardId(null); } }} style={{
              background: boardsView ? "#eef1fd" : "transparent",
              border: `1px solid ${boardsView ? "#3b5bdb" : "#e8e8e4"}`,
              color: boardsView ? "#3b5bdb" : "#666",
              borderRadius: 8, padding: "7px 14px", cursor: "pointer", fontWeight: 600, fontSize: 13, fontFamily: "Inter, sans-serif",
            }}>
              📁 Boards {boards.length > 0 && `(${boards.length})`}
            </button>
          )}
          {onLogout && (
            <button onClick={onLogout} style={{ background: "transparent", border: "1px solid #e8e8e4", color: "#999", borderRadius: 8, padding: "7px 14px", cursor: "pointer", fontWeight: 500, fontSize: 12, fontFamily: "Inter, sans-serif" }}>
              Sign out
            </button>
          )}
        </div>

        {/* Mobile hamburger + dropdown */}
        <div className="pts-mobile-toggle" ref={menuRef} style={{ position: "relative" }}>
          <button
            onClick={() => setMenuOpen(v => !v)}
            aria-label="Open menu"
            style={{
              background: menuOpen ? "#eef1fd" : "transparent",
              border: `1px solid ${menuOpen ? "#3b5bdb" : "#e8e8e4"}`,
              borderRadius: 8, padding: "8px 10px", cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
              width: 40, height: 38,
            }}
          >
            <div style={{ display: "flex", flexDirection: "column", gap: 3.5 }}>
              <span style={{ width: 18, height: 2, background: menuOpen ? "#3b5bdb" : "#444", borderRadius: 1 }} />
              <span style={{ width: 18, height: 2, background: menuOpen ? "#3b5bdb" : "#444", borderRadius: 1 }} />
              <span style={{ width: 18, height: 2, background: menuOpen ? "#3b5bdb" : "#444", borderRadius: 1 }} />
            </div>
          </button>
          {menuOpen && (
            <div style={{
              position: "absolute", top: "calc(100% + 8px)", right: 0,
              minWidth: 240, background: "#fff",
              border: "1px solid #e8e8e4", borderRadius: 12,
              boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
              padding: 10, zIndex: 50,
              animation: "slideDown 0.15s ease",
            }}>
              {userName && (
                <div style={{ padding: "8px 10px 10px", borderBottom: "1px solid #f0f0ee", marginBottom: 6 }}>
                  <div style={{ fontSize: 11, color: "#999", fontWeight: 500, marginBottom: 2 }}>Signed in as</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "#1a1a1a" }}>{userName} 👋</div>
                </div>
              )}
              <div style={{ padding: "8px 10px", marginBottom: 4 }}>
                {(() => {
                  const monthly = typeof usage.monthlyCredits === "number" ? usage.monthlyCredits : 50;
                  const pct = usage.unlimited ? 100 : (typeof usage.balance === "number" ? (usage.balance / monthly) * 100 : 100);
                  const color = usage.unlimited ? "#0a7c4e" : (pct <= 20 ? "#dc2626" : pct <= 50 ? "#b45309" : "#64748b");
                  const bg = usage.unlimited ? "#ecfdf5" : (pct <= 20 ? "#fef2f2" : pct <= 50 ? "#fffbeb" : "#f7f7f5");
                  const border = usage.unlimited ? "#a7f3d0" : (pct <= 20 ? "#fecaca" : pct <= 50 ? "#fde68a" : "#e8e8e4");
                  return (
                    <span style={{
                      display: "inline-flex", alignItems: "center", gap: 6,
                      fontSize: 12, fontWeight: 600, color, background: bg,
                      border: `1px solid ${border}`, borderRadius: 20, padding: "3px 10px",
                    }}>
                      {usage.badge && <span>{usage.badge}</span>}
                      {usage.unlimited
                        ? `${usage.tierLabel || "Unlimited"} · ∞`
                        : `${usage.balance} credits · ${usage.tierLabel || ""}`}
                    </span>
                  );
                })()}
              </div>
              <button
                onClick={() => { setShowFavs(v => !v); setMenuOpen(false); }}
                style={{
                  display: "block", width: "100%", textAlign: "left",
                  background: showFavs ? "#eef1fd" : "transparent",
                  border: "none", color: showFavs ? "#3b5bdb" : "#1a1a1a",
                  borderRadius: 8, padding: "10px 12px", cursor: "pointer",
                  fontWeight: 600, fontSize: 14, fontFamily: "Inter, sans-serif",
                }}
              >
                ★ {showFavs ? "Hide saved" : "Saved topics"}{favs.length > 0 && ` (${favs.length})`}
              </button>
              {showFavs && favs.length > 0 && (
                <button
                  onClick={() => { exportFavs(); setMenuOpen(false); }}
                  style={{
                    display: "block", width: "100%", textAlign: "left",
                    background: "transparent", border: "none", color: "#666",
                    borderRadius: 8, padding: "10px 12px", cursor: "pointer",
                    fontWeight: 500, fontSize: 14, fontFamily: "Inter, sans-serif",
                  }}
                >
                  ⬇ Export saved as CSV
                </button>
              )}
              <button
                onClick={() => { setMenuOpen(false); window.location.href = "/guide"; }}
                style={{
                  display: "block", width: "100%", textAlign: "left",
                  background: "transparent", border: "none", color: "#1a1a1a",
                  borderRadius: 8, padding: "10px 12px", cursor: "pointer",
                  fontWeight: 500, fontSize: 14, fontFamily: "Inter, sans-serif",
                }}
              >
                📖 How to use Guide
              </button>
              {(usage.tier === "standard" || usage.tier === "pro") && (
                <button
                  onClick={async () => {
                    setMenuOpen(false);
                    try {
                      const res = await fetch("/api/create-portal", {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ email: userEmail }),
                      });
                      const data = await res.json();
                      if (data.url) {
                        window.location.href = data.url;
                      } else {
                        alert("Could not open billing portal. Try again in a moment.");
                      }
                    } catch {
                      alert("Could not open billing portal. Try again in a moment.");
                    }
                  }}
                  style={{
                    display: "block", width: "100%", textAlign: "left",
                    background: "transparent", border: "none", color: "#1a1a1a",
                    borderRadius: 8, padding: "10px 12px", cursor: "pointer",
                    fontWeight: 500, fontSize: 14, fontFamily: "Inter, sans-serif",
                  }}
                >
                  💳 Manage subscription
                </button>
              )}
              {onLogout && (
                <button
                  onClick={() => { setMenuOpen(false); onLogout(); }}
                  style={{
                    display: "block", width: "100%", textAlign: "left",
                    background: "transparent", border: "none", color: "#dc2626",
                    borderRadius: 8, padding: "10px 12px", cursor: "pointer",
                    fontWeight: 500, fontSize: 14, fontFamily: "Inter, sans-serif",
                    borderTop: "1px solid #f0f0ee", marginTop: 4, paddingTop: 12,
                  }}
                >
                  Sign out
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="pts-body" style={{ flex: 1, maxWidth: 820, margin: "0 auto", width: "100%", padding: "28px 20px" }}>

        {/* Search panel */}
        {!showFavs && !boardsView && (
          <div className="pts-search-panel" style={{ background: "#fff", border: "1px solid #e8e8e4", borderRadius: 12, padding: 22, marginBottom: 24, boxShadow: "0 1px 4px rgba(0,0,0,0.04)" }}>
            <div style={{ fontSize: 11, color: "#3b5bdb", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 14 }}>Find PDF Topics</div>
            <div className="pts-search-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
              <div>
                <label style={{ fontSize: 12, color: "#999", display: "block", marginBottom: 5, fontWeight: 500 }}>Pick a niche</label>
                <select value={niche} onChange={e => { setNiche(e.target.value); setCustom(""); }}
                  style={{ width: "100%", background: "#fff", border: "1px solid #e8e8e4", borderRadius: 8, padding: "9px 12px", fontSize: 14, color: "#1a1a1a", cursor: "pointer" }}>
                  <option value="">— Select a niche —</option>
                  {NICHES.map(n => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontSize: 12, color: "#999", display: "block", marginBottom: 5, fontWeight: 500 }}>Or search a topic</label>
                <input placeholder="e.g. passive income, gut health…" value={search}
                  onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key === "Enter" && fetchTopics()}
                  style={{ width: "100%", background: "#fff", border: "1px solid #e8e8e4", borderRadius: 8, padding: "9px 12px", fontSize: 14, color: "#1a1a1a" }} />
              </div>
            </div>
            {niche === "Other (type your own)" && (
              <div style={{ marginBottom: 14 }}>
                <input placeholder="Type your niche e.g. Beekeeping for beginners" value={custom} onChange={e => setCustom(e.target.value)}
                  style={{ width: "100%", background: "#fff", border: "1px solid #3b5bdb", borderRadius: 8, padding: "9px 12px", fontSize: 14, color: "#1a1a1a" }} />
              </div>
            )}
            <button onClick={() => fetchTopics()} disabled={loading} style={{
              width: "100%", background: loading ? "#c5cff7" : "#3b5bdb", color: "#fff",
              border: "none", borderRadius: 8, padding: 11, fontWeight: 600, fontSize: 14,
              cursor: loading ? "not-allowed" : "pointer", fontFamily: "Inter, sans-serif",
            }}>
              {loading ? "Finding your PDF topics…" : "Generate 10 Topics"}
            </button>
            {error && <div style={{ marginTop: 10, color: "#dc2626", fontSize: 13 }}>{error}</div>}
          </div>
        )}

        {/* Results bar */}
        {list.length > 0 && !boardsView && (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10, marginBottom: 14 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <div style={{ fontSize: 13, color: "#999", fontWeight: 500 }}>
                {showFavs ? `${favs.length} saved topic${favs.length !== 1 ? "s" : ""}` : `${list.length} PDF topics · ${activeNiche || search}`}
              </div>
              {/* Pro feature: priority generation badge. Cosmetic for now —
                  at current scale there's no real queue contention. The
                  label exists to signal value to paying users. */}
              {usage.priority && !showFavs && (
                <span title="Priority generation queue (Pro / Founder Lifetime)" style={{
                  display: "inline-flex", alignItems: "center", gap: 4,
                  fontSize: 10, fontWeight: 700, color: "#7c3aed",
                  background: "#f5f3ff", border: "1px solid #ddd6fe",
                  borderRadius: 20, padding: "2px 8px", letterSpacing: "0.04em", textTransform: "uppercase",
                }}>
                  ⚡ Priority
                </span>
              )}
            </div>
            {/* Pro feature: hide low-sellability topics. Only renders for
                tiers that have the canHideLowSellability flag (Pro and
                Founder Lifetime). Off by default — flip to filter the list
                down to the ≥80 sellability picks only. */}
            {usage.canHideLowSellability && !showFavs && (
              <button
                onClick={() => setHideLow(v => !v)}
                title="Show only the strongest-sellability picks (≥80)"
                style={{
                  display: "inline-flex", alignItems: "center", gap: 6,
                  background: hideLow ? "#eef1fd" : "#fff",
                  border: `1px solid ${hideLow ? "#3b5bdb" : "#e8e8e4"}`,
                  color: hideLow ? "#3b5bdb" : "#666",
                  borderRadius: 8, padding: "5px 12px", cursor: "pointer",
                  fontWeight: 600, fontSize: 12, fontFamily: "Inter, sans-serif",
                }}
              >
                {hideLow ? "✓ Showing ≥80 only" : "Hide low-sellability"}
              </button>
            )}
          </div>
        )}

        {/* Loading */}
        {loading && !boardsView && <LoadingScreen />}

        {/* Boards view — Pro/Founder Lifetime only. Renders instead of the
            topic cards when boardsView is true. Has two modes:
            (1) activeBoardId is null → list of all boards as cards
            (2) activeBoardId set → topics inside that specific board */}
        {boardsView && (
          <div style={{ animation: "fadeIn 0.3s ease" }}>
            {activeBoardId === null ? (
              <>
                {/* Boards list header */}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, flexWrap: "wrap", gap: 10 }}>
                  <div style={{ fontSize: 13, color: "#999", fontWeight: 500 }}>
                    {boards.length === 0
                      ? "No boards yet"
                      : `${boards.length} board${boards.length !== 1 ? "s" : ""}`}
                  </div>
                  <button
                    onClick={createBoard}
                    disabled={boardLoading}
                    style={{
                      background: "#3b5bdb", color: "#fff", border: "none",
                      borderRadius: 8, padding: "8px 16px", cursor: boardLoading ? "wait" : "pointer",
                      fontWeight: 600, fontSize: 13, fontFamily: "Inter, sans-serif",
                    }}
                  >
                    + New Board
                  </button>
                </div>

                {/* Boards grid */}
                {boards.length === 0 ? (
                  <div style={{
                    background: "#fff", border: "1px dashed #c5cff7",
                    borderRadius: 12, padding: "40px 24px", textAlign: "center",
                  }}>
                    <div style={{ fontSize: 36, marginBottom: 12 }}>📁</div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: "#1a1a1a", marginBottom: 6 }}>
                      Organize topics into project boards
                    </div>
                    <div style={{ fontSize: 13, color: "#666", lineHeight: 1.6, maxWidth: 420, margin: "0 auto 18px" }}>
                      Group topics for a series, a launch, or any project. Click <strong>+ New Board</strong> to create your first one.
                    </div>
                  </div>
                ) : (
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 12 }}>
                    {boards.map(b => (
                      <div key={b.id} style={{
                        background: "#fff", border: "1px solid #e8e8e4",
                        borderRadius: 12, padding: "16px 16px 14px",
                        display: "flex", flexDirection: "column", gap: 8,
                        boxShadow: "0 1px 3px rgba(0,0,0,0.03)",
                      }}>
                        <div style={{ fontSize: 24 }}>📁</div>
                        <div style={{ fontSize: 14, fontWeight: 700, color: "#1a1a1a", lineHeight: 1.3 }}>{b.name}</div>
                        <div style={{ fontSize: 12, color: "#999" }}>
                          {b.count || 0} topic{(b.count || 0) !== 1 ? "s" : ""}
                        </div>
                        <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
                          <button
                            onClick={() => openBoard(b.id)}
                            style={{
                              flex: 1, background: "#eef1fd", border: "none",
                              color: "#3b5bdb", fontWeight: 600, fontSize: 12,
                              borderRadius: 6, padding: "6px 10px", cursor: "pointer",
                              fontFamily: "Inter, sans-serif",
                            }}
                          >
                            Open
                          </button>
                          <button
                            onClick={() => deleteBoard(b.id, b.name)}
                            title="Delete board"
                            style={{
                              background: "transparent", border: "1px solid #fecaca",
                              color: "#dc2626", fontWeight: 600, fontSize: 12,
                              borderRadius: 6, padding: "6px 10px", cursor: "pointer",
                              fontFamily: "Inter, sans-serif",
                            }}
                          >
                            ✕
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <>
                {/* Board detail header */}
                {(() => {
                  const board = boards.find(b => b.id === activeBoardId);
                  return (
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, flexWrap: "wrap", gap: 10 }}>
                      <button
                        onClick={() => { setActiveBoardId(null); setActiveBoardTopics([]); }}
                        style={{
                          background: "transparent", border: "1px solid #e8e8e4",
                          color: "#666", borderRadius: 8, padding: "7px 14px",
                          cursor: "pointer", fontWeight: 600, fontSize: 12, fontFamily: "Inter, sans-serif",
                        }}
                      >
                        ← All boards
                      </button>
                      <div style={{ fontSize: 15, fontWeight: 800, color: "#1a1a1a", flex: 1, textAlign: "center", minWidth: 120 }}>
                        📁 {board?.name || "Board"}
                      </div>
                      <div style={{ fontSize: 12, color: "#999", minWidth: 60, textAlign: "right" }}>
                        {activeBoardTopics.length} topic{activeBoardTopics.length !== 1 ? "s" : ""}
                      </div>
                    </div>
                  );
                })()}

                {/* Board topics list */}
                {boardLoading ? (
                  <div style={{ textAlign: "center", padding: 24, color: "#999", fontSize: 13 }}>Loading…</div>
                ) : activeBoardTopics.length === 0 ? (
                  <div style={{
                    background: "#fff", border: "1px dashed #e8e8e4",
                    borderRadius: 12, padding: "32px 20px", textAlign: "center",
                    fontSize: 13, color: "#888",
                  }}>
                    This board is empty. Add topics from your search results using the 📁 button on each topic card.
                  </div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {activeBoardTopics.map(topic => (
                      <div key={topic} style={{
                        background: "#fff", border: "1px solid #e8e8e4",
                        borderRadius: 10, padding: "12px 14px",
                        display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
                      }}>
                        <div style={{ fontSize: 14, color: "#1a1a1a", fontWeight: 500, lineHeight: 1.4 }}>{topic}</div>
                        <button
                          onClick={() => removeTopicFromBoard(topic)}
                          title="Remove from board"
                          style={{
                            background: "transparent", border: "none",
                            color: "#999", fontSize: 18, cursor: "pointer",
                            padding: "4px 8px", lineHeight: 1, flexShrink: 0,
                          }}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Cards — gated on !boardsView */}
        {!boardsView && (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {list.map((topic, i) => (
              <div key={topic} style={{ animation: `fadeIn 0.25s ease ${i * 0.03}s both`, position: "relative" }}>
                <TopicCard
                  topic={topic}
                  isFav={favs.includes(topic)}
                  onFav={toggleFav}
                  insightData={insights[topic]}
                  onToggleInsight={handleToggleInsight}
                  outline={outlines[topic]}
                  outlineLoading={outlineLoading[topic]}
                  onOutline={() => handleOutline(topic)}
                  onDownloadPDF={downloadOutlineAsPDF}
                />
                {/* Add-to-board picker — Pro/Founder only. Renders as a small
                    button anchored to the top-right of each topic card; click
                    it to open a dropdown listing the user's boards. */}
                {usage.canHideLowSellability && (
                  <div className="pts-board-picker" style={{ position: "absolute", top: 12, right: 56 }}>
                    <button
                      onClick={() => setAddToBoardFor(addToBoardFor === topic ? null : topic)}
                      title="Add to board"
                      style={{
                        background: addToBoardFor === topic ? "#eef1fd" : "transparent",
                        border: `1px solid ${addToBoardFor === topic ? "#3b5bdb" : "transparent"}`,
                        color: addToBoardFor === topic ? "#3b5bdb" : "#aaa",
                        borderRadius: 6, padding: "3px 7px", cursor: "pointer",
                        fontSize: 14, lineHeight: 1, fontFamily: "Inter, sans-serif",
                      }}
                    >
                      📁
                    </button>
                    {addToBoardFor === topic && (
                      <div style={{
                        position: "absolute", top: "100%", right: 0, marginTop: 4,
                        background: "#fff", border: "1px solid #e8e8e4",
                        borderRadius: 10, padding: 6, minWidth: 180,
                        boxShadow: "0 8px 24px rgba(0,0,0,0.12)", zIndex: 50,
                      }}>
                        <div style={{ fontSize: 10, color: "#999", fontWeight: 700, padding: "4px 8px", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                          Add to board
                        </div>
                        {boards.length === 0 ? (
                          <div style={{ fontSize: 12, color: "#888", padding: "8px 8px" }}>
                            No boards yet. Create one from the Boards tab.
                          </div>
                        ) : (
                          boards.map(b => (
                            <button
                              key={b.id}
                              onClick={() => addTopicToBoard(b.id, topic)}
                              style={{
                                display: "block", width: "100%", textAlign: "left",
                                background: "transparent", border: "none",
                                color: "#1a1a1a", fontWeight: 500, fontSize: 13,
                                borderRadius: 6, padding: "7px 8px", cursor: "pointer",
                                fontFamily: "Inter, sans-serif",
                              }}
                              onMouseEnter={e => e.currentTarget.style.background = "#f4f4f0"}
                              onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                            >
                              📁 {b.name}
                            </button>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Empty states — only when NOT in boards view */}
        {topics.length === 0 && !loading && !showFavs && !boardsView && (
          <div style={{ display: "flex", flexDirection: "column", gap: 28, animation: "fadeIn 0.4s ease" }}>

            {/* Quick-start chips */}
            <div>
              <div style={{ fontSize: 11, color: "#3b5bdb", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 12 }}>
                ⚡ Quick start — popular niches
              </div>
              <div style={{ fontSize: 13, color: "#999", marginBottom: 14 }}>
                One click and we'll generate 10 sellable PDF topics for that niche.
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {QUICK_START_NICHES.map(n => (
                  <button
                    key={n}
                    onClick={() => { setNiche(n); setSearch(""); setCustom(""); fetchTopics(n); }}
                    style={{
                      background: "#fff", border: "1px solid #e8e8e4", borderRadius: 20,
                      padding: "8px 14px", fontSize: 13, color: "#1a1a1a", cursor: "pointer",
                      fontFamily: "Inter, sans-serif", fontWeight: 500, transition: "all 0.15s",
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = "#eef1fd"; e.currentTarget.style.borderColor = "#3b5bdb"; e.currentTarget.style.color = "#3b5bdb"; }}
                    onMouseLeave={e => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.borderColor = "#e8e8e4"; e.currentTarget.style.color = "#1a1a1a"; }}
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div>

            {/* What you get with each topic */}
            <div>
              <div style={{ fontSize: 11, color: "#3b5bdb", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 12 }}>
                What you get with every topic
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }}>
                {[
                  { icon: "📊", title: "Sellability score", desc: "0–98 score weighing 10 factors: virality, competition, buyer intent, urgency, evergreen value." },
                  { icon: "🎯", title: "Target audience", desc: "Exactly who buys this — age, income, pain point, where they hang out online." },
                  { icon: "📑", title: "Full chapter outline", desc: "One click generates a chapter-by-chapter outline you can drop straight into Canva." },
                ].map((c, i) => (
                  <div key={i} style={{ background: "#fff", border: "1px solid #e8e8e4", borderRadius: 12, padding: 18 }}>
                    <div style={{ fontSize: 24, marginBottom: 8 }}>{c.icon}</div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: "#1a1a1a", marginBottom: 4 }}>{c.title}</div>
                    <div style={{ fontSize: 13, color: "#666", lineHeight: 1.55 }}>{c.desc}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Saved favorites preview (only if any exist) */}
            {favs.length > 0 && (
              <div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                  <div style={{ fontSize: 11, color: "#3b5bdb", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" }}>
                    ★ Your saved topics ({favs.length})
                  </div>
                  <button
                    onClick={() => setShowFavs(true)}
                    style={{ background: "none", border: "none", color: "#3b5bdb", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "Inter, sans-serif", padding: 0 }}
                  >
                    View all →
                  </button>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {favs.slice(0, 3).map(f => (
                    <div key={f} style={{ background: "#fff", border: "1px solid #e8e8e4", borderRadius: 10, padding: "12px 14px", fontSize: 14, color: "#1a1a1a", display: "flex", alignItems: "center", gap: 10 }}>
                      <span style={{ color: "#f59e0b", fontSize: 14 }}>★</span>
                      <span style={{ flex: 1 }}>{f}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Pro tip footer */}
            <div style={{
              background: "#eef1fd", border: "1px solid #c5cff7", borderRadius: 12,
              padding: "14px 18px", fontSize: 13, color: "#1e3a8a", lineHeight: 1.6,
            }}>
              <strong>Pro tip:</strong> Topics scoring <strong>85+</strong> tend to convert best on Gumroad and Etsy. Aim for under-saturated niches with high buyer intent — the score does that math for you.
            </div>

          </div>
        )}
        {showFavs && favs.length === 0 && (
          <div style={{ textAlign: "center", padding: "70px 0" }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>★</div>
            <div style={{ fontSize: 15, color: "#999", fontWeight: 500 }}>No saved topics yet</div>
            <div style={{ fontSize: 13, color: "#ccc", marginTop: 6 }}>Hit ☆ on any topic to save it here</div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div style={{ borderTop: "1px solid #e8e8e4", padding: "18px 28px", background: "#fff", textAlign: "center" }}>
        <span style={{ fontSize: 13, color: "#999" }}>
          <strong style={{ color: "#1a1a1a", fontWeight: 700 }}>PDFTopicScout</strong>
          <span style={{ color: "#ccc", margin: "0 6px" }}>by</span>
          <strong style={{ color: "#3b5bdb", fontWeight: 700 }}>Creatovate</strong>
          <span style={{ color: "#ccc", margin: "0 8px" }}>—</span>
          <span style={{ color: "#aaa" }}>We help creators monetize - every way possible</span>
        </span>
        <div style={{ display: "flex", gap: 16, justifyContent: "center", marginTop: 8 }}>
          <button onClick={() => { window.location.href = "/privacy"; }} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 12, color: "#aaa", fontFamily: "Inter, sans-serif", padding: 0 }}>Privacy Policy</button>
          <span style={{ color: "#ddd" }}>·</span>
          <button onClick={() => { window.location.href = "/terms"; }} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 12, color: "#aaa", fontFamily: "Inter, sans-serif", padding: 0 }}>Terms of Service</button>
          <span style={{ color: "#ddd" }}>·</span>
          <a href="mailto:info@creatovate.biz" style={{ fontSize: 12, color: "#aaa", textDecoration: "none", fontFamily: "Inter, sans-serif" }}>info@creatovate.biz</a>
        </div>
      </div>

      {/* Top-up modal — credit pack purchase + optional Standard→Pro upgrade.
          Renders only when topUpOpen is true. Anchored as the last child of
          the outer div so it overlays everything on z-index. */}
      {topUpOpen && (
        <div
          onClick={() => !topUpLoading && setTopUpOpen(false)}
          style={{
            position: "fixed", inset: 0, background: "rgba(20,20,20,0.55)",
            display: "flex", alignItems: "center", justifyContent: "center",
            zIndex: 1000, padding: 20,
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: "#fff", borderRadius: 16, maxWidth: 480, width: "100%",
              padding: "28px 28px 24px", boxShadow: "0 24px 60px rgba(0,0,0,0.25)",
              position: "relative",
            }}
          >
            {/* Close button */}
            <button
              onClick={() => !topUpLoading && setTopUpOpen(false)}
              disabled={!!topUpLoading}
              style={{
                position: "absolute", top: 14, right: 14,
                background: "none", border: "none", cursor: topUpLoading ? "not-allowed" : "pointer",
                fontSize: 22, color: "#999", lineHeight: 1, padding: 4,
              }}
              aria-label="Close"
            >
              ×
            </button>

            <div style={{ fontSize: 18, fontWeight: 800, color: "#1a1a1a", marginBottom: 4 }}>
              {usage.tier === "trial" ? "Pick a plan to keep going" : "Top up credits"}
            </div>
            <div style={{ fontSize: 13, color: "#666", marginBottom: 20, lineHeight: 1.5 }}>
              {usage.unlimited
                ? "You're on Founder Lifetime — you don't need more credits. But you can still buy packs if you'd like."
                : usage.tier === "trial"
                  ? `You have ${usage.balance} trial credit${usage.balance === 1 ? "" : "s"} left. Pick a monthly plan — cancel anytime.`
                  : `You have ${usage.balance} credit${usage.balance === 1 ? "" : "s"} left. Pick a pack — credits roll over for a full year.`}
            </div>

            {/* Trial users see subscription CTAs. Subscribers (standard/pro/founder)
                see credit pack cards. Different goals, different flows. */}
            {usage.tier === "trial" ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 16 }}>
                {[
                  { type: "sub_standard", name: "Standard", price: "$7.99/mo", credits: "50 credits/mo", highlight: false },
                  { type: "sub_pro", name: "Pro", price: "$12.99/mo", credits: "100 credits/mo · ⚡ priority · filters", highlight: true },
                ].map(p => (
                  <button
                    key={p.type}
                    onClick={() => handleBuyPack(p.type)}
                    disabled={!!topUpLoading}
                    style={{
                      background: p.highlight ? "#3b5bdb" : "#fff",
                      border: `1.5px solid ${p.highlight ? "#3b5bdb" : "#e8e8e4"}`,
                      color: p.highlight ? "#fff" : "#1a1a1a",
                      borderRadius: 12, padding: "14px 16px",
                      textAlign: "left", cursor: topUpLoading ? "not-allowed" : "pointer",
                      fontFamily: "Inter, sans-serif",
                      display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
                      opacity: topUpLoading && topUpLoading !== p.type ? 0.5 : 1,
                    }}
                  >
                    <div>
                      <div style={{ fontSize: 15, fontWeight: 800 }}>{p.name}</div>
                      <div style={{ fontSize: 12, opacity: 0.85, marginTop: 2 }}>{p.credits}</div>
                    </div>
                    <div style={{ fontSize: 15, fontWeight: 700, whiteSpace: "nowrap" }}>
                      {topUpLoading === p.type ? "Redirecting…" : p.price}
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 16 }}>
                {[
                  { type: "pack_50", credits: 50, price: "$9.99", per: "$0.20 / credit" },
                  { type: "pack_150", credits: 150, price: "$24.99", per: "$0.17 / credit", best: true },
                ].map(p => (
                  <button
                    key={p.type}
                    onClick={() => handleBuyPack(p.type)}
                    disabled={!!topUpLoading}
                    style={{
                      background: "#fff",
                      border: `1.5px solid ${p.best ? "#3b5bdb" : "#e8e8e4"}`,
                      borderRadius: 12, padding: "16px 14px",
                      textAlign: "left", cursor: topUpLoading ? "not-allowed" : "pointer",
                      fontFamily: "Inter, sans-serif", position: "relative",
                      opacity: topUpLoading && topUpLoading !== p.type ? 0.5 : 1,
                      transition: "transform 0.15s",
                    }}
                  >
                    {p.best && (
                      <span style={{
                        position: "absolute", top: -8, right: 10,
                        background: "#3b5bdb", color: "#fff",
                        fontSize: 9, fontWeight: 800, letterSpacing: "0.04em",
                        padding: "2px 7px", borderRadius: 10, textTransform: "uppercase",
                      }}>
                        Best Value
                      </span>
                    )}
                    <div style={{ fontSize: 20, fontWeight: 900, color: "#1a1a1a" }}>{p.credits} credits</div>
                    <div style={{ fontSize: 18, fontWeight: 700, color: "#3b5bdb", marginTop: 2 }}>
                      {topUpLoading === p.type ? "Redirecting…" : p.price}
                    </div>
                    <div style={{ fontSize: 11, color: "#999", marginTop: 4 }}>{p.per}</div>
                  </button>
                ))}
              </div>
            )}

            {/* Upgrade option — only relevant for Standard subscribers */}
            {usage.tier === "standard" && (
              <div style={{ borderTop: "1px solid #f0f0ed", paddingTop: 16 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#666", marginBottom: 8, letterSpacing: "0.04em", textTransform: "uppercase" }}>
                  Or upgrade your plan
                </div>
                <button
                  onClick={handleUpgradeToPro}
                  disabled={!!topUpLoading}
                  style={{
                    width: "100%",
                    background: "#1a1a1a", color: "#fff", border: "none",
                    borderRadius: 10, padding: "12px 16px",
                    fontWeight: 700, fontSize: 14,
                    cursor: topUpLoading ? "not-allowed" : "pointer",
                    fontFamily: "Inter, sans-serif",
                    opacity: topUpLoading && topUpLoading !== "upgrade" ? 0.5 : 1,
                  }}
                >
                  {topUpLoading === "upgrade"
                    ? "Upgrading…"
                    : "⚡ Upgrade to Pro — $12.99/mo (100 credits, priority, filters)"}
                </button>
                <div style={{ fontSize: 11, color: "#999", marginTop: 8, lineHeight: 1.4 }}>
                  Stripe will charge the prorated difference for the rest of this month. Future months bill at $12.99.
                </div>
              </div>
            )}

            <div style={{ fontSize: 11, color: "#aaa", textAlign: "center", marginTop: 16 }}>
              🔒 Secure Stripe checkout · Credits arrive within seconds of payment
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
